import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';

interface SidebarContextType {
  showSidebar: boolean;
  toggleSidebar: (value?: boolean) => void;
  /**
   * Monotonic counter, bumped by {@link SidebarContextType.hintNavExpand}. A counter
   * rather than a boolean so two hints in a row both reach the rail — a boolean would
   * need a reset round-trip and the second hint would be swallowed.
   */
  navExpandHint: number;
  /** Ask the collapsed rail to draw brief attention to its expand button. Used when
   *  something OTHER than the user collapsed the nav, so the nav doesn't just vanish. */
  hintNavExpand: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider = ({ children }: { children: React.ReactNode }) => {
  // 767px (not 768) so the mobile cutover matches `useIsMobile` (`< 768`) and the
  // rails' `md:` breakpoint (≥768): at exactly 768px the desktop rail shows, so
  // mobile-only behaviors (auto-close on item click, tap scrim) must NOT fire.
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [showSidebar, setShowSidebar] = useState(!isMobile);

  useEffect(() => {
    setShowSidebar(!isMobile);
  }, [isMobile]);

  const [navExpandHint, setNavExpandHint] = useState(0);

  const toggleSidebar = (value?: boolean) => {
    if (value !== undefined) {
      setShowSidebar(value);
    } else {
      setShowSidebar(prev => !prev);
    }
  };

  // Stable identity: callers fire this from mount effects that must not re-run.
  const hintNavExpand = useCallback(() => setNavExpandHint(prev => prev + 1), []);

  return (
    <SidebarContext.Provider value={{ showSidebar, toggleSidebar, navExpandHint, hintNavExpand }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebarContext must be used within a SidebarProvider');
  }
  return context;
};
